// Local diagnostics only: fixed labels, bounded samples, no request/result data.
const SURFACES = new Set(['browser_status', 'native_operational', 'staff_10s', 'provider_30s', 'screen_30s', 'staff_realtime',
  'native_host', 'native_pickup', 'native_requests', 'native_reservations', 'native_tables',
  'native_workday', 'native_employees', 'native_menu', 'native_reservation_settings', 'native_rewards_settings',
  'native_backoffice', 'native_profile', 'native_floorplan', 'native_party']);
const OPERATIONS = new Set(['status_read', 'lease', 'poll', 'projection_read']);
const OUTCOMES = new Set(['completed', 'error', 'stale', 'released']);
const monotonicNow = () => typeof performance !== 'undefined' ? performance.now() : Date.now();
const disabled = Object.freeze({ begin: () => ({ finish() {} }), event() {},
  snapshot: () => ({ enabled: false, rows: [], samples: [] }) });

function createOperationalTelemetry({ enabled = false, maxSamples = 128, now = monotonicNow } = {}) {
  if (!enabled) return disabled;
  if (!Number.isInteger(maxSamples) || maxSamples < 1 || maxSamples > 256) throw Error('Sample bound must be 1..256');
  const startedAt = now(), rows = new Map(), samples = [];
  const rowFor = (surface, operation) => {
    surface = SURFACES.has(surface) ? surface : 'other';
    operation = OPERATIONS.has(operation) ? operation : 'other';
    const key = `${surface}:${operation}`;
    if (!rows.has(key)) rows.set(key, { surface, operation, starts: 0, completions: 0,
      active: 0, maxActive: 0, overlaps: 0, skipped: 0, coalesced: 0,
      completed: 0, error: 0, stale: 0, released: 0, durationTotalMs: 0 });
    return rows.get(key);
  };
  return {
    begin(surface, operation) {
      const row = rowFor(surface, operation), at = now();
      row.starts++; row.active++;
      if (row.active > 1) row.overlaps++;
      row.maxActive = Math.max(row.maxActive, row.active);
      let finished = false;
      return { finish(outcome = 'released') {
        if (finished) return;
        finished = true;
        outcome = OUTCOMES.has(outcome) ? outcome : 'released';
        const durationMs = Math.max(0, now() - at);
        row.active--; row.completions++; row[outcome]++; row.durationTotalMs += durationMs;
        if (samples.length === maxSamples) samples.shift();
        samples.push({ surface: row.surface, operation: row.operation, durationMs, outcome });
      } };
    },
    event(surface, operation, event) {
      if (event !== 'skipped' && event !== 'coalesced') return;
      rowFor(surface, operation)[event]++;
    },
    snapshot() {
      return { enabled: true, elapsedMs: Math.max(0, now() - startedAt), sampleLimit: maxSamples,
        rows: [...rows.values()].map(row => ({ ...row })), samples: samples.map(sample => ({ ...sample })) };
    },
  };
}

let localTelemetry;
function getLocalOperationalTelemetry(enabled = false) {
  if (!enabled) return disabled;
  if (!localTelemetry) localTelemetry = createOperationalTelemetry({ enabled: true });
  return localTelemetry;
}

// A restart invalidates the old response but waits for its transport to settle.
// Interval ticks are skipped; immediate/action refreshes coalesce into one trailing read.
function createSingleFlightPoll({ request, onResult = () => {}, onError = () => {}, isCurrent = () => true,
  telemetry = disabled, surface = 'browser_status', operation = 'status_read' }) {
  let active = false, generation = 0, flight = null, queued = false;
  const refresh = (reason = 'action') => {
    if (!active || !isCurrent()) return Promise.resolve();
    if (flight) {
      if (reason === 'interval') telemetry.event(surface, operation, 'skipped');
      else { queued = true; telemetry.event(surface, operation, 'coalesced'); }
      return flight;
    }
    const epoch = generation;
    const current = () => active && epoch === generation && isCurrent();
    let measure;
    // Defer execution until flight owns the slot, including synchronous request throws.
    flight = Promise.resolve().then(() => {
      if (!current()) return;
      measure = telemetry.begin(surface, operation);
      return request();
    }).then(result => {
      if (!measure) return;
      if (!current()) { measure.finish('stale'); return; }
      onResult(result, current);
      measure.finish('completed');
    }).catch(error => {
      if (!current()) { if (measure) measure.finish('stale'); return; }
      if (measure) measure.finish('error');
      onError(error, current);
    }).finally(() => {
      flight = null;
      if (queued && active && isCurrent()) { queued = false; refresh('action'); }
    });
    return flight;
  };
  return {
    refresh,
    start() { active = true; generation++; return refresh('immediate'); },
    stop() { active = false; generation++; queued = false; },
  };
}

module.exports = { createOperationalTelemetry, getLocalOperationalTelemetry, createSingleFlightPoll };
